import { Skeleton } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';
import { SubmitHandler } from 'react-hook-form';
import { NavLink, redirect, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { permissions, PermissionsKeyedShape } from '../../../../../permissions';
import RolesAPI, { RoleUpdateRequest } from '../../api/roles';
import {
	ROLE_BREADCRUMB_HOME,
	ROLE_FORM_NOT_FOUND,
	ROLE_FORM_UPDATE_ROLE_TITLE,
} from '../../data/constants';
import { RoleForm } from './components';
import { RoleCreateForm } from './components/RoleForm';

const EditRole = () => {
	const authData = useAuthContext();
	const { tenantHostname, roleName } = useParams();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;

	const { data, isLoading } = RolesAPI.useRoles(
		[RolesAPI.ROLES_KEY, tenantId || tenantHostname],
		tenantHostname,
		authToken,
	);

	const currentRole = data?.roles.find((role) => role.name === roleName);
	const displayName = currentRole?.displayName;
	const currentPermissions = currentRole?.permissions;

	const permissionDefaults = Object.keys(permissions).reduce(
		(acc, key: keyof PermissionsKeyedShape) => {
			const availableRoles = permissions[key];
			const rolesToAdd = availableRoles.filter((role) =>
				currentPermissions?.includes(role),
			);
			acc[key] = rolesToAdd;
			return acc;
		},
		{} as PermissionsKeyedShape,
	);

	const { mutate } = RolesAPI.useUpdateRole(
		[RolesAPI.ROLES_KEY, tenantId || tenantHostname],
		tenantHostname,
		authToken,
		() =>
			tenantHostname
				? redirect(`/admin/tenants/${tenantHostname}/roles`)
				: redirect('/admin/roles'),
	);

	const onSubmit: SubmitHandler<RoleCreateForm> = (data) => {
		const updateRequest = {} as RoleUpdateRequest;
		updateRequest.name = data.name;
		updateRequest.displayName = data.displayName;
		updateRequest.assignPermissions = [];
		Object.keys(data).forEach((key: keyof RoleCreateForm) => {
			if (key !== 'name' && key !== 'permissions' && key !== 'displayName') {
				updateRequest.assignPermissions =
					updateRequest.assignPermissions.concat(data[key]);
			}
		});
		updateRequest.replaceAll = true;
		mutate(updateRequest);
	};

	return (
		<>
			<Breadcrumbs aria-label='breadcrumb'>
				<Link
					component={NavLink}
					underline='hover'
					color='inherit'
					to={
						tenantHostname
							? `/admin/tenants/${tenantHostname}/roles`
							: '/admin/roles'
					}
				>
					{ROLE_BREADCRUMB_HOME()}
				</Link>
				<Typography color='text.primary'>{roleName}</Typography>
			</Breadcrumbs>
			{currentRole && data && !isLoading && (
				<RoleForm
					edit
					onSubmit={onSubmit}
					formTitle={ROLE_FORM_UPDATE_ROLE_TITLE()}
					roleName={roleName}
					displayName={displayName}
					permissions={permissions}
					permissionDefaults={permissionDefaults}
				/>
			)}
			{!currentRole && !isLoading && (
				<Typography>{ROLE_FORM_NOT_FOUND()}</Typography>
			)}
			{isLoading && <Skeleton height={56} width='100%' />}
		</>
	);
};

export default EditRole;
